import { CFI, parseCfi, parseCfiRange } from "epubjs";
import { PageBreak } from "../../apps/reader/framework/api/features/pagebreaks/types";
import { binarySearch } from "./sort";

/**
 * Comparators assume full paths with doc and path. Add a fake
 * path to doc-only cfis. Looking for "!" to avoid the overhead
 * of cfi parses.
 * @param cfi
 */
export function getExtendedCfi(cfi) {
  if (!cfi) {
    return cfi;
  }
  return cfi.indexOf("!") === -1 ? `${cfi}!/4` : cfi;
}

/**
 * Look for a cfi match within the provided cfi's content document,
 * falling backwards to the nearest item for that doc if an exact match
 * is not found. Return the first item on that page if there is no
 * previous match for that CFI. -1 if no content for that page is available
 * or bad data provided.
 *
 * Duplicate locations are not permissible and behavior is subject to change.
 *
 * This is intentionally constructed as a single-pass operation due to
 * potentially large data sets.
 */

export function findIndexMatchingDocAndCfiFallBackToBefore(
  orderedItems,
  getItemCfi,
  cfi
) {
  if (orderedItems.length === 0 || !cfi) {
    return -1;
  }

  const parsedCfi = parseCfi(cfi);
  for (let i = 0; i < orderedItems.length - 1; i++) {
    const itemCfi = getItemCfi(orderedItems[i]);
    const firstItemCfi = parseCfi(itemCfi);
    if (!parsedCfi.contentDoc || !firstItemCfi.contentDoc) {
      return -1;
    }
    if (CFI.equals(parsedCfi.contentDoc, firstItemCfi.contentDoc)) {
      const compareThis = CFI.compare(parsedCfi, firstItemCfi);
      const compareNext = CFI.compare(
        parsedCfi,
        parseCfi(getItemCfi(orderedItems[i + 1]))
      );
      if (compareThis === 0 || compareNext < 0) {
        return i;
      }
    }
  }
  return -1;
}

/**
 * Look for a cfi match. If not found, get the index that is
 * before the requested cfi
 *
 * Duplicate locations are not permissible and behavior is subject to change.
 *
 * This is intentionally constructed as a single-pass operation due to
 * potentially large data sets.
 */
export function findIndexMatchingCfiFallBackToBefore(
  orderedItems,
  getItemCfi,
  cfi
) {
  if (!orderedItems || orderedItems.length === 0 || !cfi) {
    return -1;
  }

  return binarySearch(
    parseCfi(cfi),
    orderedItems,
    (parsedCfi, midItem) => {
      return CFI.compare(parsedCfi, parseCfi(getItemCfi(midItem)));
    },
    true
  );
}

/**
 * find page label for a cfi from an ordered set of page breaks
 * fast binary search suitable for large datasets
 */
export function findPageLabelFromCfi(pageBreaks: PageBreak[], cfi: string) {
  if (!pageBreaks?.length) {
    return "";
  }
  const matchIdx = binarySearch(
    parseCfi(cfi),
    pageBreaks,
    (targetCfi, pageBreak) => {
      const curCfi = parseCfi(pageBreak.extendedCfi);
      return CFI.compare(targetCfi, curCfi);
    },
    true
  );
  // fall back to start of page cfi is on
  return pageBreaks[matchIdx]?.label ?? "";
}

export function isValidCfi(cfi: string) {
  try {
    parseCfi(cfi);
    return true;
  } catch {
    return false;
  }
}

export function isValidCfiRange(cfiRange: string) {
  try {
    parseCfiRange(cfiRange);
    return true;
  } catch {
    return false;
  }
}

export function safeParseCfi(cfi: string) {
  try {
    return parseCfi(cfi);
  } catch {
    return null;
  }
}

export function safeParseCfiRange(cfiRange: string) {
  try {
    return parseCfiRange(cfiRange);
  } catch {
    return null;
  }
}

/**
 * For legacy snippets we need to support inclusive ranges.
 * The easiest way to do this is to increment the final number by 1.
 *
 * This only supports character offsets and CFI steps. Temporal and spatial offsets
 * are out of scope because there is no way to know what the indended step size is.
 *
 * A cfi range must always have an end, so we can rely on this
 * being the only part we need to check.
 */
export function convertToInclusiveCfiRange(
  cfiRange: epubjs.CFIRange
): epubjs.CFIRange {
  const copyOfRange = parseCfiRange(cfiRange.toString());
  if (copyOfRange.end.charOffset) {
    copyOfRange.end.charOffset.offset = copyOfRange.end.charOffset.offset + 1;
  } else if (copyOfRange.end.temporalOffset || copyOfRange.end.spatialOffset) {
    // do nothing
  } else {
    if (copyOfRange?.end.steps && copyOfRange?.end.steps.length) {
      copyOfRange.end.steps[copyOfRange?.end.steps.length - 1].step =
        copyOfRange?.end.steps[copyOfRange?.end.steps.length - 1].step + 1;
    }
  }
  return copyOfRange;
}

export function getStartCfiFromRange(cfiRange: string) {
  try {
    return safeParseCfiRange(cfiRange)?.start;
  } catch {
    return null;
  }
}

export function isCfiInsideRange(cfi: string, cfiRange: string): boolean {
  const cfiObj = safeParseCfi(cfi);
  const rangeObj = safeParseCfiRange(cfiRange);

  if (cfiObj && rangeObj) {
    return (
      CFI.compare(rangeObj.start, cfiObj) <= 0 &&
      CFI.compare(rangeObj.end, cfiObj) >= 0
    );
  }
  return false;
}

export function getCfiContentDocIdref(cfi: string) {
  const cfiObj = safeParseCfi(cfi);
  const contentDoc = cfiObj?.contentDoc;
  if (!cfiObj || !contentDoc) {
    return null;
  }

  if (contentDoc.steps[1].assert) {
    const assertion = contentDoc.steps[1].assert;
    const [ns, idref] = assertion.split("=");
    return idref || ns;
  }

  return null;
}

export function getDocCorrectedCfi(
  cfi: string,
  correctionMap: { [cfi: string]: string }
) {
  const cfiToCheck = safeParseCfi(cfi);
  if (cfiToCheck) {
    const idref = getCfiContentDocIdref(cfiToCheck.toString());
    if (idref && correctionMap[idref]) {
      const newContentDoc = safeParseCfi(correctionMap[idref]);
      if (newContentDoc && !CFI.equals(cfiToCheck.contentDoc, newContentDoc)) {
        cfiToCheck.contentDoc = newContentDoc;
        cfi = cfiToCheck.toString();
      }
    }
  }

  return cfi;
}
